import { useQueryClient } from '@tanstack/react-query';
import { observer } from 'mobx-react';
import { useState } from 'react';

import { AxiosError } from 'axios';
import { Firm } from 'types/firm';
import { BaseModalProps } from 'types/firmInfo';

import { Modal, Spacer, makeStyles } from '@bequestinc/wui';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';

import { FirmsQueryKey } from 'hooks/query-hooks/firms/useFirmsQuery';
import {
  useDeleteOfficeMutation,
  useDeleteVirtualOfficeMutation,
} from 'hooks/query-hooks/offices/useOfficeMutation';

import parseErrors from 'utils/errors';

const useStyles = makeStyles()({
  root: {
    width: '80%',
    maxWidth: 1000,
    margin: 'auto',
  },
  button: {
    marginTop: '30px',
    marginLeft: '50px',
  },
});

interface PropTypes extends BaseModalProps {
  officeId: number;
  isPhysicalOffice: boolean;
  firmId: number;
}

const DeleteOfficeInfoModal = ({
  open,
  onClose,
  officeId,
  isPhysicalOffice,
  firmId,
}: PropTypes) => {
  const { classes } = useStyles();
  const queryClient = useQueryClient();
  const [error, setError] = useState('');

  const deleteFromCache = () => {
    if (isPhysicalOffice) {
      queryClient.setQueryData<Firm>([...FirmsQueryKey, firmId], oldData => ({
        ...oldData,
        offices: oldData.offices.filter(office => office.id !== officeId),
      }));
    } else {
      queryClient.setQueryData<Firm>([...FirmsQueryKey, firmId], oldData => ({
        ...oldData,
        virtualOffices: oldData.virtualOffices.filter(office => office.id !== officeId),
      }));
    }
  };

  const onSuccess = () => {
    setError('');
    onClose();
    deleteFromCache();
  };

  const onError = (e: AxiosError) => {
    setError(parseErrors(e.response));
  };

  const deleteOfficeMutation = useDeleteOfficeMutation({ onSuccess, onError });
  const deleteVirtualOfficeMutation = useDeleteVirtualOfficeMutation({ onSuccess, onError });

  return (
    <Modal title="Remove Office?" open={open} onClose={onClose} className={classes.root}>
      <Grid container>
        <Grid item xs={12}>
          <Typography>
            Once removed, this office location will no longer be viewable to members.
          </Typography>
        </Grid>
        <Grid item>
          <Button variant="contained" onClick={onClose} className={classes.button}>
            No, Keep Office
          </Button>
        </Grid>
        <Grid item>
          <Button
            variant="contained"
            color="primary"
            onClick={() =>
              isPhysicalOffice
                ? deleteOfficeMutation.mutateAsync(officeId)
                : deleteVirtualOfficeMutation.mutateAsync(officeId)
            }
            className={classes.button}
          >
            Yes, Delete Office
          </Button>
        </Grid>
      </Grid>
      <Spacer v={16} />
      {error && (
        <Typography fontWeight="bold" color="error" style={{ whiteSpace: 'pre-line' }}>
          {error}
        </Typography>
      )}
    </Modal>
  );
};

export default observer(DeleteOfficeInfoModal);
